<template>
  <div :class="wrapStyle" :style="cssVars">
    <!-- TODO：当设计稿等流程就绪，开放面板搜索/组名称的能力 -->
    <!-- <menu-header
      :nodes="nodes"
      :index="index"
      :panels-config="panelsConfig"
      @search="handleSearch"
    /> -->
    <div ref="vnodeContainer" :class="ns.e('group-title')" />
    <jd-scrollbar
      :key="menuId"
      tag="ul"
      role="menu"
      :class="[ns.b(), { [ns.m('has-group-title')]: isShowGroupTitle }]"
      :wrap-class="ns.e('wrap')"
      :view-class="[
        ns.e('list'),
        ns.is('empty', isEmpty),
        { [ns.em('list', 'has-group-title')]: isShowGroupTitle },
      ]"
      @mousemove="handleMouseMove"
      @mouseleave="clearHoverZone"
    >
      <jd-checkbox
        v-if="multipleAll"
        :class="ns.e('multiple-all')"
        :label="multipleAllText"
        :disabled="multipleAllDisabled"
        :model-value="checkedAll"
        :indeterminate="indeterminateAll"
        @update:model-value="handleCheckAllChange"
        @click.stop
      />
      <!-- TODO：当设计稿等流程就绪，开放面板搜索/组名称的能力 -->
      <!-- v-for="node in searchNodes && searchedNodes ? searchedNodes : nodes" -->
      <jd-cascader-node
        v-for="node in nodes"
        :key="node.uid"
        :node="node"
        :menu-id="menuId"
        @expand="handleExpand"
      />
      <div v-if="isLoading" :class="ns.e('empty-text')">
        <jd-icon size="14" :class="ns.is('loading')">
          <loading />
        </jd-icon>
        {{ t('el.cascader.loading') }}
      </div>
      <div v-else-if="isEmpty" :class="ns.e('empty-text')">
        <slot name="empty">{{ t('el.cascader.noData') }}</slot>
      </div>
      <svg
        v-else-if="panel?.isHoverMenu"
        ref="hoverZone"
        :class="ns.e('hover-zone')"
      />
    </jd-scrollbar>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  onMounted,
  ref,
  render,
} from 'vue'
import { Loading } from '@jd/jdesign-icons-vue'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import { generateId } from '@jdesign-vue/utils'
import JdIcon from '@jdesign-vue/components/icon'
import JdCascaderNode from './node.vue'
// import MenuHeader from './menu-header.vue'
import { CASCADER_PANEL_INJECTION_KEY } from './types'

import type { default as CascaderNode, PanelsConfig } from './node'
import type { PropType } from 'vue'
import type { Nullable } from '@jdesign-vue/utils'

export default defineComponent({
  name: 'JdCascaderMenu',

  components: {
    Loading,
    JdIcon,
    JdScrollbar,
    JdCascaderNode,
    // MenuHeader,
  },

  props: {
    nodes: {
      type: Array as PropType<CascaderNode[]>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    checkedNodesCount: Number,
    panelsConfig: Object as PropType<PanelsConfig>,
  },

  setup(props) {
    const instance = getCurrentInstance()!
    const ns = useNamespace('cascader-menu')

    const { t } = useLocale()
    const id = generateId()
    let activeNode: Nullable<HTMLElement> = null
    let hoverTimer: Nullable<number> = null

    const panel = inject(CASCADER_PANEL_INJECTION_KEY)!

    const hoverZone = ref<null | SVGSVGElement>(null)
    const searchNodes = ref('')
    const searchedNodes = ref<CascaderNode[]>([])

    const isEmpty = computed(() => !props.nodes.length)
    const isLoading = computed(() => !panel.initialLoaded)
    const menuId = computed(() => `cascader-menu-${id}-${props.index}`)

    const multipleAllCount = computed(() => panel.multipleAllCount)
    const checkedNodesCountText = computed(() =>
      multipleAllCount.value && props.checkedNodesCount && props.index === 0
        ? `（${props.checkedNodesCount}）`
        : ''
    )

    const multipleAll = computed(() => {
      const baseCondition = panel.multipleAll && panel.config?.multiple
      if (!baseCondition) return false
      if (!panel.config.checkStrictly) {
        return panel.multipleAllLevel || props.index === 0
      }
      return true
    })
    const multipleAllText = computed(
      () =>
        `${
          (panel.multipleAllText === '全部'
            ? t('el.cascader.all')
            : panel.multipleAllText) ||
          t('el.cascader.all') ||
          '全部'
        }${checkedNodesCountText.value}`
    )
    const multipleAllDisabled = computed(
      () =>
        panel.multipleAllDisabled ||
        (panel.multipleLimit > 0 &&
          panel.checkedNodes.length >= panel.multipleLimit)
    )
    const wrapStyle = computed(() => {
      const panelWidth = panel.config?.panelWidth
      if (panelWidth) {
        return [ns.b('wrapper'), ns.is('panelWidth', panelWidth !== '')]
      }
      return [ns.b('wrapper')]
    })

    const cssVars = computed(() => {
      const panelWidth = panel.config?.panelWidth
      if (panelWidth) {
        return {
          '--panel-width':
            typeof panelWidth === 'number' ? `${panelWidth}px` : panelWidth,
        }
      }
      return {}
    })

    const handleExpand = (e: MouseEvent) => {
      activeNode = e.target as HTMLElement
    }

    const checkedAll = computed(() =>
      props.nodes.every((node: CascaderNode) => node.checked)
    )
    const indeterminateAll = computed(
      () =>
        props.nodes.some(
          (node: CascaderNode) => node.checked || node.indeterminate
        ) && !checkedAll.value
    )

    const handleSearch = (searchValue: string, resultValue: CascaderNode[]) => {
      searchNodes.value = searchValue
      searchedNodes.value = resultValue
    }

    const handleCheckAllChange = (checked: boolean) => {
      props.nodes.forEach((node: CascaderNode) => {
        if (!node.config.expandDisabled && node.isDisabled) {
          return
        }
        panel.handleCheckChange(node, checked)
      })
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!panel.isHoverMenu || !activeNode || !hoverZone.value) return

      if (activeNode.contains(e.target as HTMLElement)) {
        clearHoverTimer()

        const el = instance.vnode.el as HTMLElement
        const { left } = el.getBoundingClientRect()
        const { offsetWidth, offsetHeight } = el
        const startX = e.clientX - left
        const top = activeNode.offsetTop
        const bottom = top + activeNode.offsetHeight

        hoverZone.value.innerHTML = `
          <path style="pointer-events: auto;" fill="transparent" d="M${startX} ${top} L${offsetWidth} 0 V${top} Z" />
          <path style="pointer-events: auto;" fill="transparent" d="M${startX} ${bottom} L${offsetWidth} ${offsetHeight} V${bottom} Z" />
        `
      } else if (!hoverTimer) {
        hoverTimer = window.setTimeout(
          clearHoverZone,
          panel.config.hoverThreshold
        )
      }
    }

    const clearHoverTimer = () => {
      if (!hoverTimer) return
      clearTimeout(hoverTimer)
      hoverTimer = null
    }

    const clearHoverZone = () => {
      if (!hoverZone.value) return
      hoverZone.value.innerHTML = ''
      clearHoverTimer()
    }
    //追加分组逻辑
    const vnodeContainer = ref(null)
    const currentVNode = ref(null)
    const isShowGroupTitle = ref(false)
    const createGroupTitleVNode = () => {
      if (panel?.config?.createGroupPanelTitle) {
        const vnode = panel?.config?.createGroupPanelTitle(
          props.index,
          props.nodes
        )
        if (currentVNode.value && vnodeContainer.value) {
          render(null, vnodeContainer.value) // 清除旧VNode
        }
        if (vnode) {
          isShowGroupTitle.value = true
          currentVNode.value = vnode
          if (currentVNode.value && vnodeContainer.value) {
            render(currentVNode.value, vnodeContainer.value) // 渲染新VNode
          }
        }
      }
    }
    onMounted(() => {
      createGroupTitleVNode()
    })
    return {
      ns,
      panel,
      hoverZone,
      isEmpty,
      isLoading,
      menuId,
      t,
      handleExpand,
      handleMouseMove,
      clearHoverZone,

      searchNodes,
      searchedNodes,
      handleSearch,

      multipleAll,
      multipleAllText,
      multipleAllDisabled,
      checkedAll,
      indeterminateAll,
      handleCheckAllChange,
      vnodeContainer,
      isShowGroupTitle,
      wrapStyle,
      cssVars,
    }
  },
})
</script>
